import { FC, useRef, useState } from 'react';

import { VideoSlice, VideoSliceDefault } from '@/types';
import * as prismicH from '@prismicio/helpers';

import classNames from 'classnames';
import { Image } from '@/components';
import { getSlicePadding } from '@/utils';
import { useIsMobile } from 'hooks/useIsMobile';
import { VideoIframe, VideoOptimizer } from './Video.component';

import playIcon from '../../public/images/video-icons/play.svg';
import pauseIcon from '../../public/images/video-icons/pause.svg';
import style from './video.module.scss';

const DefaultVariation: FC<{
  slice: VideoSlice & VideoSliceDefault;
  aspectRatio: string;
}> = ({ slice, aspectRatio }) => {
  const { isMobile } = useIsMobile();

  const marginBottom = slice?.primary?.bottom_spacing;
  const videoSrc = prismicH.asLink(slice?.primary?.video_content);
  const poster = slice?.primary?.preview_image;
  const maxHeight = isMobile
    ? slice?.primary?.max_height_mobile
    : slice?.primary?.max_height_desktop;
  const paddingSection = slice?.primary?.padding_section;
  const controls = slice?.primary?.controls;
  const background = slice?.primary?.section_background_color ?? '';
  const autoplay = slice?.primary?.autoplay;
  const videoOnFullWidth = slice?.primary.video_on_full_width;

  const maxHeightCss = maxHeight ? `${maxHeight}px` : undefined;

  const [play, setPlay] = useState(autoplay);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const ytVideo = videoSrc?.includes('youtube');

  const handleClick = () => {
    if (controls !== 'Simplified') {
      return;
    }
    if (!play) {
      videoRef?.current?.play();
      setPlay(true);
      return;
    }
    setPlay(false);
    videoRef?.current?.pause();
  };

  const renderVideoContent = () => {
    if (ytVideo) {
      //const ytVideoSrc = autoplay ? `${videoSrc}?autoplay=1&mute=1` : videoSrc;

      return (
        <div
          className={classNames(style.video__wrapper, {
            [style.video__wrapper_not_image]: !videoSrc && !poster?.url,
            [style.video__wrapper_container]: !videoOnFullWidth,
          })}
          style={{ height: maxHeightCss, aspectRatio }}
        >
          <VideoIframe
            autoplay={autoplay}
            maxHeight={maxHeightCss}
            muted={autoplay}
            title="YouTube video player"
            videoUrl={videoSrc || ''}
          />
        </div>
      );
    }

    return (
      <div
        className={classNames(style.video__wrapper, {
          [style.video__wrapper_not_image]: !videoSrc && !poster?.url,
          [style.video__wrapper_container]: !videoOnFullWidth,
        })}
        onClick={handleClick}
        role="button"
        style={{ maxHeight: maxHeightCss }}
      >
        <VideoOptimizer
          alt="Video content"
          ariaLabel="Video player with custom controls"
          aspectRatio={aspectRatio}
          autoplay={autoplay}
          controls={controls}
          enableKeyboardControls={true}
          isPlaying={play}
          loop={true}
          maxHeight={maxHeightCss}
          muted={true}
          onPause={() => setPlay(false)}
          onPlay={() => setPlay(true)}
          onPlayPause={handleClick}
          playsInline={true}
          poster={poster}
          videoRef={videoRef}
          videoUrl={videoSrc || ''}
        />
        {controls !== 'Hide' && (
          <div
            className={classNames(style.overlay, {
              [style.play__state]: play,
              [style.pause__state]: !play,
              [style.has__controls]: controls === 'Detailed',
            })}
          >
            <figure>
              <Image
                alt="play icon"
                className={style.icon}
                height={60}
                src={play ? pauseIcon : playIcon}
                width={60}
              />
            </figure>
          </div>
        )}
      </div>
    );
  };

  return (
    <section
      className={classNames(style.video, getSlicePadding(paddingSection), {
        ['spacing_bottom']: marginBottom,
      })}
      style={{ background }}
    >
      <div
        className={classNames(style.video__container_full_width, {
          [style.video__container]: !videoOnFullWidth,
        })}
      >
        {renderVideoContent()}
      </div>
    </section>
  );
};

export default DefaultVariation;
